import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import en from '../../../../lang/en-us';
import { SubmitButtonWithLoader } from '../../../../lib/Button';
import { OtpField, ResendOtp, useTimer } from '../../../../lib/OtpField';
import {
    generateLockUnlockEmailOtp,
    generateLockUnlockMobileOtp,
    verifyLockUnlockOtp,
    submitFolioLockUnlock,
    getLockUnlockStatus,
} from '../../../../redux-store/actions/nct';
import { onResendLoginOTP as resendOtp } from '../../../../redux-store/actions/login';
import { RootStateType } from '../../../../redux-store/reducers';
import { connect } from 'react-redux';
import { FolioLockInfo } from './types';
import { ActionHeader } from '..';
import { maskEmail, maskMobileNumber } from '../../../../utils/utilityFunctions';
import { addDialCode } from '../../../../utils/constants';
import { RegisterWith } from '../../../../redux-store/types/login';
import './lock-unlock-folios.scss';

const { dashboard } = en;

const FOLIO_LOCK_UNLOCK_EMAIL = 'FOLIO_LOCK_UNLOCK_EMAIL';
const FOLIO_LOCK_UNLOCK_MOBILE = 'FOLIO_LOCK_UNLOCK_MOBILE';

const otpInit = new Array(6).fill('');

type OtpCall = 'email' | 'mobile';

interface LocationState {
    selectedFolios: FolioLockInfo[];
    operation: 'L' | 'U';
    trxnType: 'F' | 'B';
}

function LockUnlockFoliosOtp(props: any) {
    const history = useHistory();
    const location = useLocation<LocationState>();
    const initialOtpType: OtpCall =
        props.userRegisteredWith === RegisterWith.Mobile ? 'mobile' : 'email';
    const [otpInput, setOtp] = useState<string[]>([...otpInit]);
    const [count, setCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [otpType, setOtpType] = useState<OtpCall>(initialOtpType);
    const [otpInfo, setOtpInfo] = useState<{ otpRef: string; reqId: string }>({
        otpRef: '',
        reqId: '',
    });

    const { timer, startTimer, resetTimer, setTimerStart } = useTimer({});

    const { selectedFolios, operation, trxnType } = location.state || ({} as LocationState);

    const buildMetaData = () => ({
        appType: 'WEB',
        OSVersion: '',
        deviceName: '',
        deviceOS: window.navigator.userAgent,
    });

    useEffect(() => {
        if (!selectedFolios || selectedFolios.length === 0) {
            history.push('/portal/nct/lock-unlock-folios');
            return;
        }

        const generateInitialOtp = async () => {
            try {
                if (initialOtpType === 'mobile') {
                    const response = await props.generateLockUnlockMobileOtp({
                        email: props.email || '',
                        mobile: props.mobile || '',
                        pan: props.pan || '',
                        pekrn: props.pekrn || '',
                        reqId: '',
                        action: FOLIO_LOCK_UNLOCK_MOBILE,
                        metaData: buildMetaData(),
                    });
                    setOtpInfo({
                        otpRef: response?.body?.otpRef || '',
                        reqId: response?.body?.reqId,
                    });
                } else {
                    const response = await props.generateLockUnlockEmailOtp({
                        email: props.email || '',
                        mobile: props.mobile || '',
                        reqId: '',
                        pan: props.pan || '',
                        pekrn: props.pekrn || '',
                        action: FOLIO_LOCK_UNLOCK_EMAIL,
                        metaData: buildMetaData(),
                    });
                    setOtpInfo({
                        otpRef: response?.body?.otpRef || '',
                        reqId: response?.body?.reqId,
                    });
                }
                setTimerStart(true);
            } catch (err: any) {
                alert(err.message || 'Failed to generate OTP. Please try again.');
                setTimerStart(true);
                history.push('/portal/nct/lock-unlock-folios');
            }
        };

        generateInitialOtp();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateSecondOtp = async () => {
        try {
            const reqId = otpInfo.reqId;
            const nextType: OtpCall = initialOtpType === 'email' ? 'mobile' : 'email';
            let response: any;
            if (nextType === 'mobile') {
                response = await props.generateLockUnlockMobileOtp({
                    email: props.email || '',
                    mobile: selectedFolios[0].mobile || '',
                    pan: props.pan || '',
                    pekrn: props.pekrn || '',
                    reqId,
                    action: FOLIO_LOCK_UNLOCK_MOBILE,
                    metaData: buildMetaData(),
                });
            } else {
                response = await props.generateLockUnlockEmailOtp({
                    email: selectedFolios[0].email || '',
                    mobile: props.mobile || '',
                    pan: props.pan || '',
                    pekrn: props.pekrn || '',
                    reqId,
                    action: FOLIO_LOCK_UNLOCK_EMAIL,
                    metaData: buildMetaData(),
                });
            }
            setOtpInfo({ otpRef: response?.body?.otpRef || '', reqId: response?.body?.reqId });
            setOtp([...otpInit]);
            setOtpType(nextType);
            resetTimer();
        } catch (err: any) {
            alert(err.message || 'Failed to send the second OTP. Please try again.');
        }
    };

    const submitLockUnlock = async () => {
        const folioPayload = {
            reqId: otpInfo.reqId,
            email: props.email || '',
            mobile: props.mobile || '',
            pan: props.pan || '',
            pekrn: props.pekrn || '',
            operation: operation === 'L' ? 'L' : 'U',
            trxnType: trxnType || '',
            data: selectedFolios.map((folio) => ({
                amc: folio.amc,
                folio: folio.folio,
            })),
        };

        const submitResponse = await props.submitFolioLockUnlock(folioPayload);
        history.push('/portal/nct/lock-unlock-folios-success', {
            operation,
            reqId: submitResponse?.body?.reqId || submitResponse?.reqId,
        });
    };

    const handleSubmit = async () => {
        const otp = otpInput.join('');
        if (otp.length !== 6) {
            alert('Please enter a valid 6-digit OTP');
            return;
        }
        if (!otpInfo.otpRef) {
            alert('OTP reference not found. Please generate a new OTP.');
            return;
        }

        setLoading(true);
        try {
            const verifyPayload = {
                email: props.email || '',
                mobile: props.mobile || '',
                pan: props.pan || '',
                pekrn: props.pekrn || '',
                enteredOtp: otp,
                otpRef: otpInfo.otpRef,
                reqId: otpInfo.reqId,
                action: otpType === 'email' ? FOLIO_LOCK_UNLOCK_EMAIL : FOLIO_LOCK_UNLOCK_MOBILE,
                metaData: buildMetaData(),
            };
            await props.verifyLockUnlockOtp(verifyPayload);

            if (otpType === initialOtpType) {
                await generateSecondOtp();
            } else {
                await submitLockUnlock();
            }
        } catch (err: any) {
            alert(err.message || 'OTP verification failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const onResend = async () => {
        try {
            const { body = {} } = await props.resendOtp({
                otpRef: otpInfo.otpRef || '',
                reqId: otpInfo.reqId || '',
                action: otpType === 'email' ? FOLIO_LOCK_UNLOCK_EMAIL : FOLIO_LOCK_UNLOCK_MOBILE,
            });
            setOtpInfo({
                otpRef: body.otpRef || otpInfo.otpRef,
                reqId: body.reqId || otpInfo.reqId,
            });
            setOtp([...otpInit]);
            resetTimer();
        } catch (err: any) {
            alert(err.message || 'Failed to resend OTP.');
        }
    };

    const operationText = operation === 'L' ? 'Lock' : 'Unlock';

    return (
        <div className="curved-bg">
            <ActionHeader
                header={`${operationText} Folios - OTP Verification`}
                subHeader={`Verify OTP to ${operationText.toLowerCase()} ${selectedFolios?.length || 0
                    } folio(s)`}
            />
            <div className="curved-bg-content">
                <img className="change-email-img" src="/images/change-otp-img.png" alt="icon" />
                <div className="curved-bg-content-inner">
                    <div className="curved-bg-content-inner-top">
                        <h3 className="subheading">
                            {dashboard.OtpScreen.subHeading}
                            <span>{dashboard.OtpScreen.subHeadingSpan}</span>
                        </h3>
                        {otpType === 'mobile' ? (
                            <p>
                                {dashboard.OtpScreen.otpPhoneSubject}{' '}
                                <span>{maskMobileNumber(props.mobile || '')}</span>
                            </p>
                        ) : (
                            <p>
                                {dashboard.OtpScreen.subHeadingInfo}{' '}
                                <span>{maskEmail(props.email || '')}</span>
                            </p>
                        )}
                        <OtpField
                            value={otpInput}
                            updateValue={(otp: string[]) => {
                                setCount(count + 1);
                                setOtp(otp);
                            }}
                        />
                    </div>
                    <div className="curved-bg-content-inner-bottom">
                        <SubmitButtonWithLoader
                            disabled={otpInput.join('').length !== 6}
                            loading={loading}
                            label={dashboard.OtpScreen.buttonText}
                            labelWhileLoading={dashboard.OtpScreen.buttonTextWhileLoading}
                            onClick={handleSubmit}
                        />
                        <ResendOtp counter={timer} onResendOtp={onResend} startTimer={startTimer} />
                    </div>
                </div>
            </div>
        </div>
    );
}

export default connect(({ authReducer }: RootStateType) => ({ ...authReducer }), {
    generateLockUnlockEmailOtp,
    generateLockUnlockMobileOtp,
    verifyLockUnlockOtp,
    submitFolioLockUnlock,
    getLockUnlockStatus,
    resendOtp,
})(LockUnlockFoliosOtp);
